import "./Home.css";
import React, { useEffect, useRef, useState } from "react";
import GlobalSearch from "../../components/GlobalSearch/GlobalSearch";
import { Box, Button, Skeleton, Stack, Typography } from "@mui/material";
import MenuCards from "../../components/MenuCards/MenuCards";
import ProductCard from "../../components/ProductCard/ProductCard";
import { ChevronLeftSharp, ChevronRightSharp, ContactMailOutlined, History, Search } from "@mui/icons-material";
import PageTitle from "../../components/PageTitle/PageTitle";
import { useSite } from "../../context/siteContext";
import { useNavigate } from "react-router-dom";
import { ASSETS_BASE_URL, DOCUMENT_EXTERNAL_ENABLED, DOCUMENT_EXTERNAL_URL } from "../../config";
import { BUTTON, LABELS, ROUTES, TITLES, UI_TEXT } from "../../constants";
import { useTranslation } from "react-i18next";
import { getBestSellersBySite } from "../../services/homeService";
import SkeletonProductCard from "../../components/SkeletonProductCard/SkeletonProductCard";
import useBrandingAssets from "../../hooks/useBrandingAssets";
import useCompanyInfo from "../../hooks/useCompanyInfo";
import { buildExternalLoginUrl } from "../../utilities/externalDocs";
import { useAuth } from "../../context/authContext";


function Home() {
	const [bestSellers, setBestSellers] = useState([]);
	const [bestSellersLoading, setBestSellersLoading] = useState(true);
	const { selectedSite, selectedSiteData } = useSite();
	const prodsRef = useRef(null);
	const navigate = useNavigate();
	const {t} = useTranslation();
	const { homeBackdropStyle } = useBrandingAssets();
	const { companyName } = useCompanyInfo();
	const { hasPermission } = useAuth();
	const canSeeTrackOrder = !!hasPermission("order_management.add_to_cart");

	const handleDocumentationNavigate = () => {
		if (!DOCUMENT_EXTERNAL_ENABLED || !DOCUMENT_EXTERNAL_URL) {
			navigate(ROUTES.DOCUMENT);
			return;
		}
		const username = selectedSiteData?.amf_method_username_ln;
		const password = selectedSiteData?.amf_method_password_ln;
		const loginUrl = buildExternalLoginUrl({
			templateUrl: DOCUMENT_EXTERNAL_URL,
			username,
			password,
		});
		if (!loginUrl) {
			navigate(ROUTES.DOCUMENT);
			return;
		}
		window.open(loginUrl, "_blank", "noopener,noreferrer");
	};

	const HomeMenuitems = [
		{ label: LABELS.MY_MACHINES, path: ROUTES.MACHINES },
		{ label: LABELS.DOCUMENT, path: ROUTES.DOCUMENT, onClick: handleDocumentationNavigate },
		{ label: LABELS.RSPL, path: ROUTES.RSPL },
		{ label: LABELS.ORDER_HISTORYS, path: ROUTES.ORDER_HISTORY },
	];

	const scrollSmoothly = (direction) => {
		if (prodsRef.current) {
			const scrollContainer = prodsRef.current;
			const scrollAmount = 400;
			let start = null;
			const initialScroll = scrollContainer.scrollLeft;
			const targetScroll = direction === "left" ? initialScroll - scrollAmount : initialScroll + scrollAmount;

			const step = (timestamp) => {
				if (!start) start = timestamp;
				const progress = timestamp - start;
				const easing = Math.min(progress / 500, 1); // Customize easing (300ms duration)
				scrollContainer.scrollLeft = initialScroll + (targetScroll - initialScroll) * easing;

				if (easing < 1) {
					window.requestAnimationFrame(step);
				}
			};

			window.requestAnimationFrame(step);
		}
	};

	useEffect(() => {
		const fetchBestSellers = async () => {
			if (!selectedSite) {
				setBestSellers([]);
				setBestSellersLoading(false);
				return;
			}
			try {
				setBestSellersLoading(true);
				const response = await getBestSellersBySite({
					site_number: selectedSite,
					limit: 25,
				});
				if (response?.status) {
					setBestSellers(response.data || []);
				} else {
					setBestSellers([]);
				}
			} catch (error) {
				console.error("Error fetching best sellers:", error);
				setBestSellers([]);
			} finally {
				setBestSellersLoading(false);
			}
		};

		fetchBestSellers();
	}, [selectedSite]);

	// Function to scroll left
	const scrollLeft = () => scrollSmoothly("left");

	// Function to scroll right
	const scrollRight = () => scrollSmoothly("right");

	return (
		<div style={{ minHeight: "80vh", paddingTop: "0px" }}>
			<div className="home-slider" style={homeBackdropStyle || { backgroundImage: `url('${ASSETS_BASE_URL}/home_bg3.jpg')` }}>
				<GlobalSearch width="70%" />
			</div>
			<div className="home-content">
				<div className="home-content-left">
					<h2>{t('UI_TEXT.REVOLUTIONIZE_SERVICE_MANAGEMENT', { company: companyName })}</h2>
					<div className="header-divider"></div>
					<p>
						{t('UI_TEXT.UPGRADRE_YOUR_MANUFACTURING_PROCESS', { company: companyName })}
					</p>

					<Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
						{t('LABELS.QUICK_ACTIONS')}
					</Typography>
					<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexWrap: 'wrap' }}>
						{canSeeTrackOrder && (
							<Button startIcon={<History />} variant="contained" className="button-primary"
								onClick={() => navigate(ROUTES.TRACK_ORDER)}>
								{t('LABELS.TRACK_ORDER')}
							</Button>
						)}
						<Button startIcon={<Search />} variant="outlined" className="button-primary"
							onClick={() => navigate(`${ROUTES.SEARCH_RESULTS}?partsonly=true`)}>
							{t('LABELS.SEARCH_PARTS')}
						</Button>
						<Button startIcon={<ContactMailOutlined />} variant="outlined" className="button-primary"
							onClick={() => navigate(ROUTES.SUPPORT)}>
							{t("LABELS.SUPPORT")}
						</Button>
					</Stack>
				</div>
				<div className="homeDividerh"></div>
				<div className="home-content-right" data-tour="main-page-action-panels">
					{HomeMenuitems.map((item, index) => (
						<MenuCards
							key={index}
							label={t(item.label)}
							path={item.path}
							onClick={item.onClick}
						/>
					))}
				</div>
			</div>
			{(bestSellersLoading || (bestSellers && bestSellers.length > 0)) && (
				<div className="home-reccommendations">
					<PageTitle title={t('TITLES.BEST_SELLERS')} />

					<div className="rec-products">
						<div className="iconLeft" onClick={scrollLeft}>
							<ChevronLeftSharp sx={{ height: "80px", width: "80px" }} />
						</div>

						<div className="prods" ref={prodsRef}>
							{bestSellersLoading ? (
								// 🔹 Skeleton cards shown during loading
								Array.from({ length: 6 }).map((_, index) => (
									<SkeletonProductCard key={index} />
								))
							) : bestSellers && bestSellers.length > 0 ? (
								bestSellers.map((product, index) => (
									<ProductCard
										key={`${product.product_id}_${index}`}
										product={product}
										machineID={product.machine_id}
										showSavedForLaterDial={true}
									/>
								))
							) : (
								// 🔹 Graceful empty state
								<Typography
									variant="body1"
									sx={{ p: 2, color: "text.secondary", textAlign: "center", width: "100%" }}
								>
									{t('UI_TEXT.NO_BEST_SELLERS_AVAILABLE')}
								</Typography>
							)}
						</div>

						<div className="iconRight" onClick={scrollRight}>
							<ChevronRightSharp sx={{ height: "80px", width: "80px" }} />
						</div>
					</div>
				</div>
			)}


		</div>
	);
}

export default Home;
